import { useState, useEffect } from "react";
import { Loader2, MapPin } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { PostalCity } from "@/components/PostalCity";
import { toast } from "@/components/ui/sonner";
import { api } from "@/lib/appresse";
import { useAuth } from "@/lib/auth";

const SNOOZE_KEY = "appresse_loc_snooze"; // session-scoped: reappears next login/session

// Asks existing users (without postal code / city) to complete their location.
// Skippable ("plus tard") but reappears on next session until filled.
export function ProfileLocationPrompt() {
  const { user, refreshUser } = useAuth();
  const [open, setOpen] = useState(false);
  const [loc, setLoc] = useState({ countryCode: "FR", country: "France", postal: "", city: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user || user.role === "admin") return;
    const missing = !user.addr_postal_code || !user.addr_city;
    const snoozed = sessionStorage.getItem(SNOOZE_KEY) === "1";
    if (missing && !snoozed) {
      setLoc((l) => ({ ...l, country: user.country_residence || "France" }));
      setOpen(true);
    }
  }, [user]);

  const later = () => { sessionStorage.setItem(SNOOZE_KEY, "1"); setOpen(false); };

  const save = async () => {
    if (!loc.postal.trim()) return toast.error("Le code postal est obligatoire");
    if (!loc.city.trim()) return toast.error("Veuillez renseigner votre ville");
    setSaving(true);
    try {
      await api.updateProfile({
        addr_postal_code: loc.postal.trim(),
        addr_city: loc.city.trim(),
        addr_country: loc.country,
        country_residence: loc.country,
      });
      await refreshUser();
      toast.success("Merci ! Votre localisation est enregistrée.");
      setOpen(false);
    } catch {
      toast.error("Échec de l'enregistrement");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) later(); }}>
      <DialogContent className="sm:max-w-md rounded-2xl" data-testid="location-prompt-dialog">
        <div className="space-y-4">
          <DialogHeader className="items-center text-center space-y-2">
            <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center">
              <MapPin className="w-7 h-7 text-primary" />
            </div>
            <DialogTitle className="font-serif font-black text-xl">Complétez votre localisation</DialogTitle>
            <DialogDescription className="text-sm text-muted-foreground">
              Indiquez votre code postal : votre ville s'affichera automatiquement. Cela nous aide à mieux vous servir.
            </DialogDescription>
          </DialogHeader>
          <PostalCity value={loc} onChange={setLoc} idPrefix="prompt" />
          <div className="flex flex-col gap-2 pt-1">
            <Button className="w-full gap-2" disabled={saving} onClick={save} data-testid="location-prompt-save">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Valider
            </Button>
            <Button variant="ghost" className="w-full text-muted-foreground" onClick={later} data-testid="location-prompt-later">
              Plus tard
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
